/**
 * US 820773 — Pure grouping logic for "Create Work Order from Standard Request".
 *
 * Kept free of any runtime imports (no API/capacitor deps) so it is trivially
 * unit-testable and can be reused independently of the useStandardRequests hook.
 *
 * Groups Standard Requests the same way the Web app does
 * (StandardRequestWOViewController.getSrGridComponents):
 *   rank 1 -> SR equipment matches the selected equipment       ("Same Equipment")
 *   rank 2 -> SR equipment class matches the selected eq class  ("Same Equipment Class")
 *   rank 3 -> everything else                                   ("Other")
 */

export type StandardRequestRank = 1 | 2 | 3;

export interface StandardRequestRow {
  cf_work_order_0: string;
  description_0?: string;
  wo_type_0?: string;
  equipment_0?: string;
  equipment_class_0?: string;
  priority_0?: string;
  criticality_0?: string;
  entered_date_0?: string;
  rank?: StandardRequestRank;
  // "SYS Standard Request" columns are query-configurable, so allow extras.
  [key: string]: any;
}

export interface StandardRequestGroup {
  rank: StandardRequestRank;
  /** Translation key the UI resolves for the group header. */
  labelKey: string;
  items: StandardRequestRow[];
}

/** Maps a rank to the i18n key used for its group header (mirrors Web labels). */
export const SR_RANK_LABEL_KEYS: Record<StandardRequestRank, string> = {
  1: "lbl_sr_same_eq",
  2: "lbl_sr_same_eqclass",
  3: "lbl_other_sr",
};

/**
 * Assigns a rank to each SR row and returns them sorted by
 * rank -> equipment class -> SR number (identical ordering to Web).
 * Pure function — no I/O.
 */
export function rankStandardRequests(
  rows: StandardRequestRow[],
  equipment?: string,
  equipmentClass?: string
): StandardRequestRow[] {
  const eq = (equipment ?? "").trim();
  const eqClass = (equipmentClass ?? "").trim();

  return rows
    .map((row) => {
      const rowEq = (row.equipment_0 ?? "").trim();
      const rowEqClass = (row.equipment_class_0 ?? "").trim();

      let rank: StandardRequestRank = 3;
      if (eq) {
        if (rowEq === eq) {
          rank = 1;
        } else if (eqClass && rowEqClass === eqClass) {
          // Note: guarded on a non-empty eqClass so SRs with a blank class are
          // not mis-grouped as "Same Equipment Class" when the selected
          // equipment has no class (a latent edge case in the Web version).
          rank = 2;
        }
      }

      return { ...row, equipment_0: rowEq, equipment_class_0: rowEqClass, rank };
    })
    .sort((a, b) => {
      if (a.rank !== b.rank) return (a.rank as number) - (b.rank as number);
      const aClass = a.equipment_class_0 ?? "";
      const bClass = b.equipment_class_0 ?? "";
      if (aClass !== bClass) return aClass < bClass ? -1 : 1;
      const aWo = a.cf_work_order_0 ?? "";
      const bWo = b.cf_work_order_0 ?? "";
      if (aWo !== bWo) return aWo < bWo ? -1 : 1;
      return 0;
    });
}

/**
 * Ranks then buckets SR rows into ordered groups, omitting empty groups.
 * Pure function.
 */
export function groupStandardRequests(
  rows: StandardRequestRow[],
  equipment?: string,
  equipmentClass?: string
): StandardRequestGroup[] {
  const ranked = rankStandardRequests(rows, equipment, equipmentClass);
  const groups: StandardRequestGroup[] = [];
  ([1, 2, 3] as StandardRequestRank[]).forEach((rank) => {
    const items = ranked.filter((r) => r.rank === rank);
    if (items.length) {
      groups.push({ rank, labelKey: SR_RANK_LABEL_KEYS[rank], items });
    }
  });
  return groups;
}
